import { Component, computed, effect, inject, input, OnInit, output, signal, WritableSignal } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { CreateMCPConfig, McpService, MCPTool, UpdateMCPConfig } from '@shared-angular/services/mcp.service';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { ManagedMCPConfig, MCPConfig, SearchDoc } from '@shared/index';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { KeyFilterModule } from 'primeng/keyfilter';
import { MessageModule } from 'primeng/message';
import { PickListModule } from 'primeng/picklist';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { RouteShareService } from 'src/app/shared/services/route-share.service';
import { ManualLinkComponent } from "../../../../../../../shared/components/rag-admin/manual-link/manual-link.component";

type ToolApproval = "always" | "never";
type GlobalToolApproval = ToolApproval | "custom";

const toolApprovalOptions: { label: string; value: ToolApproval }[] = [
  { label: $localize`:@@mcp-approval-always:Immer`, value: "always" },
  { label: $localize`:@@mcp-approval-never:Nie`, value: "never" }
];

const globalToolApprovalOptions: { label: string; value: GlobalToolApproval; disabled?: boolean }[] = [
  ...toolApprovalOptions,
  { label: $localize`:@@mcp-approval-custom:Benutzerdefiniert`, value: "custom", disabled: true }
];

@Component({
  selector: 'app-mcp-add-edit-managed',
  imports: [ButtonModule, DrawerModule, FloatLabelModule, FormsModule, InputTextModule, KeyFilterModule, ManualLinkComponent, MessageModule, PickListModule, ReactiveFormsModule, SelectModule, TableModule, ToggleSwitchModule, TooltipModule],
  templateUrl: './mcp-add-edit-managed.component.html',
  styleUrl: './mcp-add-edit-managed.component.scss'
})
export class McpAddEditManagedComponent implements OnInit {
  public readonly visible = input<boolean>(false);
  public readonly visibleChange = output<boolean>();

  // Muss dem von OpenAI geforderten Muster fuer tools[].server_label entsprechen: ^[A-Za-z][A-Za-z0-9_-]*$
  public readonly serverLabelPattern: RegExp = /^[A-Za-z][A-Za-z0-9_-]*$/;

  public readonly edit = input.required<boolean>();
  public readonly mcpConfig = input<MCPConfig>();

  public form: FormGroup;

  public serverTools = signal<MCPTool[]>([]);
  public selectedServerTools = signal<MCPTool[]>([]);
  public suggestedNtfyTopic = signal<string | null>(null);

  public readonly toolApprovalOptions = [...toolApprovalOptions];
  public readonly globalToolApprovalOptions = [...globalToolApprovalOptions];
  public toolApprovals = signal<Record<string, ToolApproval>>({});

  public globalToolApproval = computed<GlobalToolApproval>(() => {
    const selected = this.selectedServerTools().map(t => t.name);
    if (selected.length === 0) return "never";
    const approvals = this.toolApprovals();
    if (selected.every(toolName => (approvals[toolName] ?? "never") === "always")) return "always";
    if (selected.every(toolName => (approvals[toolName] ?? "never") === "never")) return "never";
    return "custom";
  });

  public rss = inject(RouteShareService);
  public mcpService = inject(McpService);

  public sourceDocuments: WritableSignal<SearchDoc[]> = signal(this.rss.documents);
  public targetDocuments: WritableSignal<SearchDoc[]> = signal([]);

  constructor(private fb: FormBuilder, private toastr: ToastrService) {
    this.form = this.fb.group({
      type: this.fb.control('managed'),
      enabled: this.fb.control(true),
      toolName: this.fb.control(undefined),
      allowed_tools: [[]],
      require_approval: this.fb.group({
        always: this.fb.group({
          tool_names: [[]]
        }),
        never: this.fb.group({
          tool_names: [[]]
        })
      })
    });

    effect(() => {
      const selected = this.selectedServerTools().map(t => t.name);
      const toolApprovals = this.toolApprovals();
      const always = selected.filter(toolName => toolApprovals[toolName] === "always");
      const never = selected.filter(toolName => toolApprovals[toolName] !== "always");

      this.form.get("allowed_tools")?.setValue(selected);
      this.form.get("require_approval")?.get("always")?.get("tool_names")?.setValue(always);
      this.form.get("require_approval")?.get("never")?.get("tool_names")?.setValue(never);
    });
  }


  public async ngOnInit(): Promise<void> {
    const mcpConfig = this.mcpConfig();
    if (this.edit() && mcpConfig && mcpConfig.type === "managed") {
      if (mcpConfig.toolName === "docsearcher") {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        this.form.addControl("documentIds", this.fb.control(mcpConfig.documentIds ?? []));
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        this.targetDocuments.set(this.rss.documents.filter(doc => this.form.get("documentIds")?.value.includes(doc.docSearchId)));
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        this.sourceDocuments.set(this.rss.documents.filter(doc => !this.form.get("documentIds")?.value.includes(doc.docSearchId)));
      }
      if (mcpConfig.toolName === "reflactTrain") {
        this.form.addControl("serverLabel", this.fb.control(mcpConfig.serverLabel, [Validators.required, Validators.pattern(this.serverLabelPattern)]));
        this.form.addControl("serverUrl", this.fb.control(mcpConfig.serverUrl, this.reflactTrainDomainValidator));
        this.form.addControl("requiresAuth", this.fb.control(mcpConfig.requiresAuth));
        this.form.addControl("accountKey", this.fb.control(mcpConfig.accountKey ?? ""));
      }
      this.form.get("enabled")?.setValue(mcpConfig.enabled);
      this.form.get("toolName")?.setValue(mcpConfig.toolName);
      if (mcpConfig.toolName === "reflactTrain") {
        await this.loadToolsFromUrl(mcpConfig.serverUrl);
      } else {
        await this.loadSelectedServerTools(mcpConfig.toolName);
      }
      this.selectedServerTools.set(this.serverTools().filter(t => {
        const mcpConfigValue = this.mcpConfig();
        if (mcpConfigValue) {
          if (mcpConfigValue.type === "managed") {
            return mcpConfigValue.allowed_tools.includes(t.name);
          } else {
            return mcpConfigValue.tool.allowed_tools.includes(t.name);
          }
        }
        return false;
      }));
      this.form.get("allowed_tools")?.setValue(mcpConfig.allowed_tools);

      const configuredApprovals: Record<string, ToolApproval> = {};
      const requireApproval = mcpConfig.require_approval;
      if (requireApproval && typeof requireApproval === "object") {
        for (const toolName of requireApproval.always?.tool_names ?? []) {
          configuredApprovals[toolName] = "always";
        }
        for (const toolName of requireApproval.never?.tool_names ?? []) {
          configuredApprovals[toolName] = "never";
        }
      }
      this.toolApprovals.set(configuredApprovals);
    }
  }

  public targetChange() {
    const docIds = this.targetDocuments().map(t => t.docSearchId);
    this.form.get("documentIds")?.setValue(docIds);
  }

  public async loadSelectedServerTools(toolName: ManagedMCPConfig["toolName"]) {
    if (toolName === "reflactTrain") {
      this.serverTools.set([]);
      this.selectedServerTools.set([]);
    } else {
      const tool = this.mcpService.managedServers.find(s => s.name === toolName);
      if (tool) {
        await this.loadToolsFromUrl(tool.serverUrl);
      }
    }
    if (toolName === "docsearcher") {
      this.form.addControl("documentIds", this.fb.control([]));
    } else {
      this.form.removeControl("documentIds");
    }
    if (toolName === "reflactTrain") {
      this.form.addControl("serverLabel", this.fb.control("", [Validators.required, Validators.pattern(this.serverLabelPattern)]));
      this.form.addControl("serverUrl", this.fb.control("", this.reflactTrainDomainValidator));
      this.form.addControl("requiresAuth", this.fb.control(false));
      this.form.addControl("accountKey", this.fb.control(""));
    } else {
      this.form.removeControl("serverLabel");
      this.form.removeControl("serverUrl");
      this.form.removeControl("requiresAuth");
      this.form.removeControl("accountKey");
    }
  }

  // reflactTrain hat keine feste Katalog-URL - Tools werden anhand der vom Admin eingegebenen URL geladen
  public async loadToolsFromUrl(serverUrl: string) {
    if (!serverUrl) {
      this.serverTools.set([]);
      this.selectedServerTools.set([]);
      return;
    }
    try {
      const res = await this.mcpService.loadMcpTools(this.rss.botConfig._id, serverUrl);
      this.serverTools.set(res);
      this.selectedServerTools.set(res);
    } catch (e: unknown) {
      console.error(e);
      this.serverTools.set([]);
      this.selectedServerTools.set([]);
    }
  }

  public suggestNtfyTopic(): void {
    const topic = `bot-${Math.random().toString(36).substring(2, 8)}-${Math.random().toString(36).substring(2, 6)}`;
    this.suggestedNtfyTopic.set(topic);
  }

  public copySuggestedTopic(): void {
    const topic = this.suggestedNtfyTopic();
    if (!topic) return;
    void navigator.clipboard.writeText(topic);
    this.toastr.info("TEXT_COPIED");
  }

  // Alle reflact-Train-Instanzen eines Bots muessen dieselbe Domain nutzen, weil der Login systemweit (nicht pro Instanz) gilt.
  // Verglichen wird nur die Domain (Hostname) der Server-URL, nicht die vollstaendige URL - abweichende Pfade sind erlaubt.
  private reflactTrainDomainValidator = (control: AbstractControl): ValidationErrors | null => {
    const domain = this.extractDomain((control.value as string | null) ?? "");
    if (!domain) return null;
    const otherDomains = this.getOtherReflactTrainDomains();
    if (otherDomains.length === 0 || otherDomains.every(d => d === domain)) return null;
    return { domainMismatch: true };
  };

  private extractDomain(url: string): string | null {
    try {
      return new URL(url.trim()).hostname.toLowerCase();
    } catch {
      return null;
    }
  }

  private getOtherReflactTrainDomains(): string[] {
    const currentId = this.mcpConfig()?._id;
    const domains = this.mcpService.mcps
      .filter((mcp): mcp is ManagedMCPConfig & { toolName: "reflactTrain" } =>
        mcp.type === "managed" && mcp.toolName === "reflactTrain" && mcp._id !== currentId)
      .map(mcp => this.extractDomain(mcp.serverUrl))
      .filter((domain): domain is string => domain != null);
    return Array.from(new Set(domains));
  }

  public getToolApproval(toolName: string): ToolApproval {
    return this.toolApprovals()[toolName] ?? "never";
  }

  public setToolApproval(toolName: string, approval: ToolApproval): void {
    this.toolApprovals.update(current => ({
      ...current,
      [toolName]: approval
    }));
  }

  // "custom" ist nur ein Anzeigewert fuer gemischte Zustaende und kein tatsaechlicher Auswahlwert.
  public setGlobalToolApproval(approval: GlobalToolApproval): void {
    if (approval === "custom") return;
    const selected = this.selectedServerTools().map(t => t.name);
    this.toolApprovals.update(current => {
      const updated = { ...current };
      for (const toolName of selected) {
        updated[toolName] = approval;
      }
      return updated;
    });
  }

  public async submit() {
    if (!this.edit()) {
      await this.mcpService.createMcp(this.rss.botConfig._id, this.form.value as CreateMCPConfig);
    } else {
      const mcpConfig = this.mcpConfig();
      if (mcpConfig) {
        await this.mcpService.updateMcp(this.rss.botConfig._id, mcpConfig._id, this.form.value as UpdateMCPConfig);
      }
    }
    this.visibleChange.emit(false);
  }
}
